import {z} from 'zod';

import {
  FinancialProductPreferenceCoordinates,
  FinancialProductPreferenceCoordinatesSchema,
} from '../../api/types/onlineOfferTypes';

const OUTBOX_VERSION = 1;
const STORAGE_KEY_PREFIX = 'online-offer-financial-product-preference';
let latestGeneration = 0;

const FinancialProductPreferenceOutboxEntrySchema = z.object({
  version: z.literal(OUTBOX_VERSION),
  generation: z.number().int().positive(),
  expectedPreferenceRevision: z.number().int().nonnegative(),
  coordinates: FinancialProductPreferenceCoordinatesSchema,
});

export type FinancialProductPreferenceOutboxEntry = z.infer<
  typeof FinancialProductPreferenceOutboxEntrySchema
>;

export type FinancialProductPreferenceOutboxScope = {
  offerId: string;
  workspace: string;
};

const storageKey = (scope: FinancialProductPreferenceOutboxScope) =>
  `${STORAGE_KEY_PREFIX}:${encodeURIComponent(scope.workspace)}:${encodeURIComponent(scope.offerId)}`;

const readStoredEntry = (
  scope: FinancialProductPreferenceOutboxScope
): FinancialProductPreferenceOutboxEntry | null => {
  try {
    const value = window.localStorage.getItem(storageKey(scope));

    if (value === null) {
      return null;
    }

    const parsed = FinancialProductPreferenceOutboxEntrySchema.safeParse(JSON.parse(value));

    if (!parsed.success) {
      window.localStorage.removeItem(storageKey(scope));
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const sameCoordinates = (
  first: FinancialProductPreferenceCoordinates,
  second: FinancialProductPreferenceCoordinates
) =>
  first.productType === second.productType &&
  first.termMonths === second.termMonths &&
  first.downPaymentPercentage === second.downPaymentPercentage &&
  first.balloonPaymentPercentage === second.balloonPaymentPercentage &&
  first.leasingTargetGroup === second.leasingTargetGroup &&
  first.annualMileage === second.annualMileage;

const isSameIntent = (
  first: FinancialProductPreferenceOutboxEntry,
  second: FinancialProductPreferenceOutboxEntry
) =>
  first.generation === second.generation && sameCoordinates(first.coordinates, second.coordinates);

const isSameAttempt = (
  first: FinancialProductPreferenceOutboxEntry,
  second: FinancialProductPreferenceOutboxEntry
) =>
  isSameIntent(first, second) &&
  first.expectedPreferenceRevision === second.expectedPreferenceRevision;

const storeEntry = (
  scope: FinancialProductPreferenceOutboxScope,
  entry: FinancialProductPreferenceOutboxEntry
) => {
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify(entry));
  } catch {
    // Browser storage may be unavailable or full. The caller still retains the
    // same entry in memory so the calculator and mounted retry path keep working.
  }

  return entry;
};

export const FinancialProductPreferenceOutbox = {
  read(scope: FinancialProductPreferenceOutboxScope) {
    return readStoredEntry(scope);
  },

  replace(
    scope: FinancialProductPreferenceOutboxScope,
    coordinates: FinancialProductPreferenceCoordinates,
    expectedPreferenceRevision: number
  ) {
    const storedGeneration = readStoredEntry(scope)?.generation ?? 0;
    latestGeneration = Math.max(latestGeneration, storedGeneration) + 1;

    return storeEntry(scope, {
      version: OUTBOX_VERSION,
      generation: latestGeneration,
      expectedPreferenceRevision,
      coordinates,
    });
  },

  updateRevision(
    scope: FinancialProductPreferenceOutboxScope,
    entry: FinancialProductPreferenceOutboxEntry,
    expectedPreferenceRevision: number
  ) {
    const current = readStoredEntry(scope);

    if (current === null || !isSameIntent(current, entry)) {
      return current;
    }

    const nextExpectedPreferenceRevision = Math.max(
      current.expectedPreferenceRevision,
      expectedPreferenceRevision
    );

    return storeEntry(scope, {
      ...current,
      expectedPreferenceRevision: nextExpectedPreferenceRevision,
    });
  },

  clear(
    scope: FinancialProductPreferenceOutboxScope,
    entry: FinancialProductPreferenceOutboxEntry
  ) {
    const current = readStoredEntry(scope);

    if (current === null || !isSameAttempt(current, entry)) {
      return false;
    }

    try {
      window.localStorage.removeItem(storageKey(scope));
    } catch {
      return false;
    }

    return true;
  },

  matchesCoordinates: sameCoordinates,
  isSameIntent,
  isSameAttempt,
};
